import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ToastTriggerContext } from 'src/context/toast.context'
import {
  ActivityLogDateRangeFilter,
  ActivityLogEntityType,
} from 'src/domain/activityLog'
import { ActivityLog, ActivityLogScopeTypeEnum } from 'src/slices/services/api'
import { useActivityLogDateRange } from './useActivityLogDateRange'
import { useApiActivityLogs } from './useApiActivityLogs'

type UseActivityLogPanelOptions = {
  orgCode: string
  entityType: ActivityLogEntityType
  scopeType: ActivityLogScopeTypeEnum
  // 未確定 (null / undefined) の間は fetch しない
  scopeId: string | null | undefined
  // 帳票日を起点にする場合のみ指定 (省略時は「今日 - 1ヶ月 〜 今日」)
  baseDate?: string
  // 閉じる際の追加処理 (TenantSalesReport の activeRegisterId クリア等)。
  // close は依存に入るため安定参照 (useCallback) を渡すこと
  onClose?: () => void
}

type UseActivityLogPanelResult = {
  isOpen: boolean
  open: () => void
  close: () => void
  logs: ActivityLog[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  dateRangeFilter: ActivityLogDateRangeFilter
  errorMessage?: string
  onLoadMore: () => Promise<void>
}

/**
 * ActivityLogPanel を使う画面で共通する「開閉 / 取得 / 日付範囲 / 追加読み込み」を集約する。
 * scopeId が確定したら fetch し、取得エラーは閉じずに errorMessage で通知する。
 */
export const useActivityLogPanel = ({
  orgCode,
  entityType,
  scopeType,
  scopeId,
  baseDate,
  onClose,
}: UseActivityLogPanelOptions): UseActivityLogPanelResult => {
  const toastContext = useContext(ToastTriggerContext)
  const toastContextRef = useRef(toastContext)
  toastContextRef.current = toastContext

  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const {
    logs,
    isLoading,
    isLoadingMore,
    hasMore,
    getActivityLogs,
    loadMore,
    clearLogs,
  } = useApiActivityLogs()
  const {
    startDate,
    endDate,
    startMinDate,
    endMaxDate,
    fromISO,
    toISO,
    onChangeDateRange,
  } = useActivityLogDateRange({ baseDate })

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => {
    setIsOpen(false)
    clearLogs()
    setErrorMessage(undefined)
    onClose?.()
  }, [clearLogs, onClose])

  useEffect(() => {
    if (!isOpen || !scopeId) return undefined
    let cancelled = false
    getActivityLogs(orgCode, entityType, {
      scopeType,
      scopeId,
      from: fromISO,
      to: toISO,
    }).then((result) => {
      if (cancelled) return
      setErrorMessage(result.success ? undefined : result.message)
    })
    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    scopeId,
    entityType,
    scopeType,
    fromISO,
    toISO,
    orgCode,
    getActivityLogs,
  ])

  const onLoadMore = useCallback(async () => {
    const result = await loadMore()
    if (!result.success) {
      toastContextRef.current.sendToast({
        variant: 'error',
        title: `追加データの取得に失敗しました: ${result.message}`,
      })
    }
  }, [loadMore])

  const dateRangeFilter: ActivityLogDateRangeFilter = {
    range: { start: startDate, end: endDate },
    minDate: startMinDate,
    maxDate: endMaxDate,
    onChange: onChangeDateRange,
  }

  return {
    isOpen,
    open,
    close,
    logs,
    isLoading,
    isLoadingMore,
    hasMore,
    dateRangeFilter,
    errorMessage,
    onLoadMore,
  }
}
